import { memo, useCallback, useEffect, useRef, ComponentPropsWithoutRef } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Icon from 'builder/components/Icon'
import { MouseClickEvent } from 'builder/modules/ui'
import { i18n as I18n } from 'builder/utils/i18n'
import { DocumentTypes, FORMATS } from 'builder/modules/constants'
import { navigate } from 'builder/modules/navigate'
import {
  actions as resumeEditorActions,
  AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
  selectors,
  DownloadDocumentHandler,
} from 'builder/modules/resumeEditor'
import { ButtonTheme } from 'builder/components/Button'
import { useAiResumeReviewHighlightState } from 'builder/components/AiFeatures/AiResumeReview'
import { EditorPanelSwitcher } from 'builder/components/EditorPanelSwitcher'
import { EditorPanelType } from 'builder/modules/generalEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useTourConfig } from 'builder/components/DocumentEditor/DocumentTour'
import { CurrentDocument } from '../../types'
import { DownloadButton } from '../DownloadButton'
import { CustomizationButton } from '../CustomizationButton/CustomizationButton'
import {
  Topbar,
  SelectTemplateButtonContainer,
  ButtonContainerRight,
  HighlightAIReviewContainer,
  HighlightAIReviewSubtitle,
  HighlightAIReviewTitle,
  HighlightAIReviewCTAButtonContainer,
  ArrowPointingUp,
} from './styles'

type Props = ComponentPropsWithoutRef<typeof Topbar> & {
  type: DocumentTypes
  currentDocument: CurrentDocument
  width?: number
  isDownloading: boolean
  handleFullSize: (event?: MouseClickEvent) => void
  handleDownloadDocument: DownloadDocumentHandler
  handleShareDocument?: () => void
  handleCombineDocuments?: () => void
  handleDownloadButtonMenuOpen?: () => void
  showResumeDistributionModal?: (format: FORMATS) => void
  isRbpStepFlowActive?: boolean
  isEditorPanelTypeSwitcherEnabled?: boolean
  handleEditorPanelTypeChange?: (type: EditorPanelType) => void
  editorPanelType?: EditorPanelType
  isAiResumeReviewFeatureActive?: boolean
  shouldShowSelectTemplateButton?: boolean
  showAIHighlight?: boolean
  handleTurnOffAIHighlight?: (value: boolean) => void
}

const RESUME_REVIEW_SWITCHER_ID = 'ai_resume_review_switcher'

export const TopbarLayout = memo((props: Props) => {
  const {
    width,
    type,
    currentDocument,
    isDownloading,
    handleFullSize,
    handleShareDocument,
    showResumeDistributionModal,
    isRbpStepFlowActive,
    isEditorPanelTypeSwitcherEnabled,
    editorPanelType = 'editor',
    handleEditorPanelTypeChange,
    isAiResumeReviewFeatureActive,
    shouldShowSelectTemplateButton,
    handleDownloadDocument,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
    showAIHighlight,
    handleTurnOffAIHighlight,
    ...rest
  } = props
  const location = useLocation()
  const dispatch = useDispatch()
  const { isTourEnabled, isTourCompleted } = useTourConfig()
  const exploreTemplateButtonRef = useRef<HTMLButtonElement>(null)
  const { isAiResumeReviewSwitcherSeen, setIsAiResumeReviewSwitcherSeen } =
    useAiResumeReviewHighlightState()

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const handleSelectTemplate = useCallback(
    (event: MouseClickEvent) => {
      trackInternalEvent(`click_select_${type}_template_button`)
      handleFullSize(event)
    },
    [handleFullSize, type],
  )

  const queryParams = new URLSearchParams(location.search)
  const exploreTemplates = queryParams.get('show_templates')

  const shouldShowAiReviewSwitcher =
    isEditorPanelTypeSwitcherEnabled && isAiResumeReviewFeatureActive

  useEffect(() => {
    if (exploreTemplates === '1') {
      const newUrlWithoutExploreParam = `${location.pathname}${location?.search?.replaceAll(
        '?show_templates=1',
        '',
      )}`
      navigate(newUrlWithoutExploreParam)
      if (exploreTemplateButtonRef.current?.click) {
        exploreTemplateButtonRef.current?.click()
      }
    }
  }, [exploreTemplates, handleSelectTemplate, location.pathname, location?.search])

  useEffect(() => {
    if (isTourEnabled && !isTourCompleted) return
    if (isEditorPanelTypeSwitcherEnabled && !isAiResumeReviewSwitcherSeen) {
      const translationKey = 'builder.resume_editor.ai_review_switcher_highlight'
      dispatch(
        resumeEditorActions.setHighlightedElement({
          title: I18n.t(`${translationKey}.title`),
          content: I18n.t(`${translationKey}.description`),
          target: `[data-highlight-id=${RESUME_REVIEW_SWITCHER_ID}]`,
          buttonLabel: I18n.t(`${translationKey}.ok_button_label`),
          skipButtonLabel: I18n.t(`${translationKey}.skip_button_label`),
          disableOverlayClose: true,
          disableCloseOnEsc: true,
          showSkipOnLast: true,
          nextButtonProps: {
            leadingIcon: <Icon.FeaturedRegular />,
            theme: ButtonTheme.alternative,
          },
          onClose: () => {
            handleEditorPanelTypeChange?.('ai_review')
            setIsAiResumeReviewSwitcherSeen(true)
            trackInternalEvent('click_check_with_ai', {
              label: 'ai_review_popup',
            })
          },
          onSkip: () => {
            setIsAiResumeReviewSwitcherSeen(true)
          },
        }),
      )
      trackInternalEvent('see_ai_review_popup')
    }
  }, [
    dispatch,
    isTourEnabled,
    isTourCompleted,
    isEditorPanelTypeSwitcherEnabled,
    isAiResumeReviewSwitcherSeen,
    handleEditorPanelTypeChange,
    setIsAiResumeReviewSwitcherSeen,
  ])

  if (autoTailoringFromPublicJobBoard.enabled) {
    return null
  }

  return (
    <Topbar previewWidth={width} {...rest}>
      <ButtonContainerRight
        $isInvisible={!shouldShowSelectTemplateButton}
        $invisibilityStrategy={isAiResumeReviewFeatureActive ? 'invisible' : 'none'}
      >
        <SelectTemplateButtonContainer data-highlight-id={AFTER_USE_EXAMPLE_HIGHLIGHT_ID}>
          <CustomizationButton onClick={handleSelectTemplate} ref={exploreTemplateButtonRef} />
        </SelectTemplateButtonContainer>
      </ButtonContainerRight>
      {shouldShowAiReviewSwitcher && (
        <ButtonContainerRight>
          <EditorPanelSwitcher
            activePanelType={editorPanelType}
            handlePanelTypeChange={handleEditorPanelTypeChange}
            theme="darker"
            isFullWidth={false}
            items={[
              {
                text: I18n.t('builder.resume_editor.preview'),
                id: 'editor',
                dataTestId: 'editor-panel-switcher-item-editor',
              },
              {
                text: I18n.t('builder.resume_editor.ai_review'),
                id: 'ai_review',
                highlightId: RESUME_REVIEW_SWITCHER_ID,
                dataTestId: 'editor-panel-switcher-item-ai-review',
              },
            ]}
            data-testid="editor-panel-switcher"
          />
          {showAIHighlight && (
            <HighlightAIReviewContainer>
              <ArrowPointingUp />
              <HighlightAIReviewTitle>Check with AI?</HighlightAIReviewTitle>
              <HighlightAIReviewSubtitle>
                Check all detailed suggestions to improve your resume anytime here.
              </HighlightAIReviewSubtitle>
              <HighlightAIReviewCTAButtonContainer
                theme="alternative"
                onClick={() => {
                  handleEditorPanelTypeChange && handleEditorPanelTypeChange('ai_review')
                  handleTurnOffAIHighlight && handleTurnOffAIHighlight(false)
                }}
              >
                Check With AI
              </HighlightAIReviewCTAButtonContainer>
            </HighlightAIReviewContainer>
          )}
        </ButtonContainerRight>
      )}
      {currentDocument && !isRbpStepFlowActive && (
        <DownloadButton
          handleDownloadDocument={handleDownloadDocument}
          handleCombineDocuments={handleCombineDocuments}
          handleShareDocument={handleShareDocument}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
          showResumeDistributionModal={showResumeDistributionModal}
          isDownloading={isDownloading}
        />
      )}
    </Topbar>
  )
})
